import { SLASummary } from '../../../../../@types/service.type';
import { OutcomeTypeCode } from '../../../../../@types/sla.type';
import { SLAContactRowData } from '../../../../../components/organisms/SLAContactRow/SLAContactRow.props';
import { COLORS } from '../../../../../configs/colors';
import {
  getSLAUrgency,
  getTerminalSLAUrgency,
  SLA_URGENCY_GRADIENTS,
} from '../../../../../utils/sla-urgency';

const SLA_TARGET_ATTENDANCE = 'attendance';
const SLA_TARGET_RESOLUTION = 'resolution';

export interface SLACalculation {
  percentage: number;
  timeText: string;
  statusColor: string;
  gradientId: string;
  gradientColors: [string, string];
  isBreached: boolean;
  isMet: boolean;
  remainingMs: number;
}

export const GRADIENT_COLORS: Record<string, [string, string]> = {
  grey: [COLORS.MediumGrey, '#bdbdbd'],
};

export const calculateSLA = (sla: SLAContactRowData | undefined): SLACalculation => {
  const noSla: SLACalculation = {
    percentage: 0,
    timeText: '--:--',
    statusColor: COLORS.MediumGrey,
    gradientId: 'grad-grey',
    gradientColors: GRADIENT_COLORS.grey,
    isBreached: false,
    isMet: false,
    remainingMs: 0,
  };

  if (!sla?.loggedSLADateTime) return noSla;

  const terminalUrgency = getTerminalSLAUrgency();

  if (sla.isBreached) {
    return {
      percentage: 0,
      timeText: '0:00',
      statusColor: terminalUrgency.color,
      gradientId: 'grad-urgency-0',
      gradientColors: SLA_URGENCY_GRADIENTS[0],
      isBreached: true,
      isMet: false,
      remainingMs: 0,
    };
  }

  if (sla.isMet) {
    return {
      percentage: 100,
      timeText: 'Met',
      statusColor: terminalUrgency.color,
      gradientId: 'grad-urgency-0',
      gradientColors: SLA_URGENCY_GRADIENTS[0],
      isBreached: false,
      isMet: true,
      remainingMs: 0,
    };
  }

  const timeoutDate = new Date(sla.loggedSLADateTime);
  if (isNaN(timeoutDate.getTime())) return noSla;

  const now = new Date();
  const remainingMs = timeoutDate.getTime() - now.getTime();

  if (remainingMs <= 0) {
    return {
      percentage: 0,
      timeText: '0:00',
      statusColor: terminalUrgency.color,
      gradientId: 'grad-urgency-0',
      gradientColors: SLA_URGENCY_GRADIENTS[0],
      isBreached: true,
      isMet: false,
      remainingMs: 0,
    };
  }

  const remainingMinutes = Math.floor(remainingMs / (1000 * 60));
  const hours = Math.floor(remainingMinutes / 60);
  const minutes = remainingMinutes % 60;
  let timeText: string;
  if (hours > 72) {
    timeText = `${Math.floor(hours / 24)}d`;
  } else if (hours >= 24) {
    timeText = `${hours}h`;
  } else if (hours >= 10) {
    timeText = `${hours}:${minutes.toString().padStart(2, '0')}`;
  } else if (hours >= 1) {
    timeText = `${hours}h${minutes}m`;
  } else {
    timeText = `${minutes}m`;
  }

  const urgencyWindowMinutes = 2880; // 48 hours
  const percentage = Math.min(100, (remainingMinutes / urgencyWindowMinutes) * 100);
  const urgency = getSLAUrgency(remainingMinutes);

  return {
    percentage,
    timeText,
    statusColor: urgency.color,
    gradientId: `grad-urgency-${urgency.level}`,
    gradientColors: SLA_URGENCY_GRADIENTS[urgency.level],
    isBreached: false,
    isMet: false,
    remainingMs,
  };
};

/**
 * Select the best SLA from a pre-filtered list.
 * Prefers not-breached over breached; picks the earliest timeoutAt among ties.
 */
const selectBestSLA = (matching: SLAContactRowData[]): SLAContactRowData | undefined => {
  if (matching.length === 0) return undefined;
  const notBreached = matching.filter((sla) => !sla.isBreached);
  if (notBreached.length > 0) {
    return notBreached.sort(
      (a, b) => new Date(a.loggedSLADateTime).getTime() - new Date(b.loggedSLADateTime).getTime()
    )[0];
  }
  return matching.sort(
    (a, b) => new Date(a.loggedSLADateTime).getTime() - new Date(b.loggedSLADateTime).getTime()
  )[0];
};

const findSLAByTargetType = (
  slas: SLAContactRowData[],
  targetTypeCode: string
): SLAContactRowData | undefined =>
  selectBestSLA(slas.filter((sla) => sla.targetTypeCode === targetTypeCode));

const findSLAByScope = (
  slas: SLAContactRowData[],
  scopePattern: RegExp
): SLAContactRowData | undefined =>
  selectBestSLA(slas.filter((sla) => scopePattern.test(sla.scopeName)));

/**
 * Convert a SLAContactRowData record to a calculateSLA-compatible shape.
 */
const calculateSLAFromRecord = (record: SLAContactRowData | undefined): SLACalculation => {
  if (!record) return calculateSLA(undefined);
  return calculateSLA(record);
};

/**
 * Convert a SLASummary (from service object) to SLAContactRowData shape
 * so it can be passed to calculateSLA.
 */
const calculateSLAFromSummary = (summary: SLASummary | undefined): SLACalculation => {
  if (!summary) return calculateSLA(undefined);

  const outcomeTypeCode = summary.slaOutcome?.outcomeTypeCode;

  return calculateSLA({
    id: 0,
    scopeName: '',
    configName: '',
    configId: 0,
    loggedSLADateTime: summary.timeoutAt,
    isChecked: false,
    isMitigated: false,
    isBreached: outcomeTypeCode === OutcomeTypeCode.Breached,
    isMet: outcomeTypeCode === OutcomeTypeCode.Met,
    slaOutcomes: [],
  });
};

/**
 * Extract attendance and resolution SLA calculations from per-service SLA records.
 * Falls back to the service-level clientSLA/opsSLA summaries when specific records aren't available.
 */
export const getAttendanceSLAs = (
  serviceSLAs: SLAContactRowData[] | undefined,
  clientSLA: SLASummary | undefined,
  opsSLA: SLASummary | undefined
): { attendance: SLACalculation; resolution: SLACalculation } => {
  if (serviceSLAs && serviceSLAs.length > 0) {
    const attendanceHasTarget = serviceSLAs.some(
      (sla) => sla.targetTypeCode === SLA_TARGET_ATTENDANCE
    );
    const resolutionHasTarget = serviceSLAs.some(
      (sla) => sla.targetTypeCode === SLA_TARGET_RESOLUTION
    );

    const attendanceRecord = attendanceHasTarget
      ? findSLAByTargetType(serviceSLAs, SLA_TARGET_ATTENDANCE)
      : findSLAByScope(serviceSLAs, /ops/i);
    const resolutionRecord = resolutionHasTarget
      ? findSLAByTargetType(serviceSLAs, SLA_TARGET_RESOLUTION)
      : findSLAByScope(serviceSLAs, /client/i);

    return {
      attendance: attendanceRecord
        ? calculateSLAFromRecord(attendanceRecord)
        : calculateSLAFromSummary(opsSLA),
      resolution: resolutionRecord
        ? calculateSLAFromRecord(resolutionRecord)
        : calculateSLAFromSummary(clientSLA),
    };
  }

  // No SLA records at all: use service-level summaries
  return {
    attendance: calculateSLAFromSummary(opsSLA),
    resolution: calculateSLAFromSummary(clientSLA),
  };
};

export const formatDuration = (ms: number): string => {
  if (ms <= 0) return '0m';
  const totalMinutes = Math.floor(ms / (1000 * 60));
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  if (hours > 72) return `${Math.floor(hours / 24)}d`;
  if (hours >= 24) return `${hours}h`;
  if (hours >= 10) return `${hours}:${minutes.toString().padStart(2, '0')}`;
  if (hours >= 1) return `${hours}h${minutes}m`;
  return `${minutes}m`;
};
